/**
 * Image processing utilities for Lotese comprovante capture.
 * Story 2.2: Câmera Guiada com Compressão e Auto-Crop
 */
import imageCompression from "browser-image-compression";

const COMPRESS_OPTIONS = {
  maxSizeMB: 0.3,
  maxWidthOrHeight: 1920,
  // useWebWorker:false de propósito: no modo worker, browser-image-compression
  // v2 faz importScripts da lib a partir do CDN jsdelivr, que o CSP do app
  // (script-src 'self' ...) BLOQUEIA → compressão falhava no upload. No main
  // thread usa a lib bundled (sem CDN). Comprimir 1 foto não trava perceptível.
  useWebWorker: false,
  fileType: "image/jpeg" as const,
};

/**
 * Compress an image file/blob to ~300KB JPEG.
 * Roda no main thread (ver nota em useWebWorker) — sem dependência de CDN.
 */
export async function compressImage(file: File | Blob): Promise<Blob> {
  const input =
    file instanceof File ? file : new File([file], "capture.jpg", { type: "image/jpeg" });
  return imageCompression(input, COMPRESS_OPTIONS);
}

// --------------------------------------------------------------------------
// RN-3 — formatos aceitos no envio de comprovante
//
// Decisão da reunião de 22/07/2026 [09:10]–[09:58]: aceitar só o que câmera de
// Android e iPhone produzem, e **restringir** o resto. Augusto: *"Ele vai mandar
// vídeo, ele vai mandar foto em formato TIFF, PDF, ele vai mandar tudo. Então,
// de fato, eu vou restringir."*
//
// ⚠️ **O navegador sempre entrega JPEG ao backend.** O `accept` inclui HEIC/HEIF
// porque é o que o iPhone produz, mas a compressão converte antes do upload
// (`fileType: "image/jpeg"`). Guardar HEIC no servidor seria pior que rejeitar:
// o OCR não lê HEIC, então o comprovante subiria, ocuparia storage e cairia em
// pendência pela RN-2 — gastando o envio do parceiro para um resultado
// garantidamente inútil.
// --------------------------------------------------------------------------

/** MIME types que a câmera de Android e iPhone produzem. */
export const FORMATOS_CAMERA = [
  "image/jpeg",
  "image/jpg", // alguns Android reportam assim
  "image/png",
  "image/webp",
  "image/heic", // iPhone (HEIF container)
  "image/heif",
] as const;

/** Valor do atributo `accept` do input de arquivo.
 *
 * Inclui HEIC de propósito: sem ele, o iOS ou esconde as fotos do carrossel ou
 * as converte silenciosamente — e quando converte, tudo bem, mas quando esconde
 * o parceiro acha que o app está quebrado. Melhor deixá-lo escolher e converter
 * aqui, onde dá pra explicar se falhar.
 */
export const ACCEPT_COMPROVANTE = FORMATOS_CAMERA.join(",");

/** Erro com mensagem já redigida PARA O PARCEIRO.
 *
 * Existe para que o chamador nunca precise inventar texto — e, principalmente,
 * para que nunca mais exista um `catch` mudo nesse caminho: o parceiro
 * escolhia a foto, o spinner girava, parava, e nada acontecia.
 */
export class ImagemInvalidaError extends Error {
  constructor(
    message: string,
    /** `true` quando outra foto resolve; `false` quando é limitação do aparelho. */
    readonly podeTentarOutraFoto: boolean = true
  ) {
    super(message);
    this.name = "ImagemInvalidaError";
  }
}

const EXTENSOES_HEIC = /\.(heic|heif)$/i;

function pareceHeic(file: File | Blob): boolean {
  const tipo = (file.type || "").toLowerCase();
  if (tipo.includes("heic") || tipo.includes("heif")) return true;
  // Alguns pickers entregam HEIC com `type` vazio — cai no nome do arquivo.
  return file instanceof File && EXTENSOES_HEIC.test(file.name);
}

/**
 * Valida o arquivo escolhido e devolve um JPEG pronto para upload.
 *
 * Faz o que o `catch {}` anterior não fazia: **diz o que houve**. Cada caminho
 * de falha tem uma mensagem que indica a AÇÃO do parceiro, porque "erro ao
 * processar imagem" o faz repetir exatamente o mesmo passo.
 *
 * @throws ImagemInvalidaError sempre com mensagem pronta para exibir.
 */
export async function prepararComprovanteParaEnvio(file: File | Blob): Promise<Blob> {
  const tipo = (file.type || "").toLowerCase();

  // Vídeo é o caso citado na reunião e o mais custoso: dezenas de MB que o
  // parceiro sobe no 4G antes de descobrir que não serve.
  if (tipo.startsWith("video/")) {
    throw new ImagemInvalidaError(
      "Isso é um vídeo. Envie uma foto do comprovante — pelo app da câmera, não a gravação."
    );
  }

  if (tipo === "application/pdf") {
    throw new ImagemInvalidaError(
      "PDF não é aceito. Tire uma foto do comprovante impresso com a câmera do celular."
    );
  }

  const ehFormatoConhecido =
    (FORMATOS_CAMERA as readonly string[]).includes(tipo) || pareceHeic(file);

  if (tipo && !ehFormatoConhecido) {
    throw new ImagemInvalidaError(
      `Formato não aceito (${tipo}). Envie uma foto tirada pela câmera do celular.`
    );
  }

  try {
    return await compressImage(file);
  } catch (e) {
    // A conversão roda em canvas. No iPhone o WebKit decodifica HEIC nativamente
    // e isso funciona; num navegador que não decodifica, falha aqui. Antes essa
    // exceção era engolida e o parceiro ficava sem imagem e sem explicação.
    if (pareceHeic(file)) {
      throw new ImagemInvalidaError(
        "Este aparelho não conseguiu converter a foto (formato HEIC do iPhone). " +
          "Em Ajustes › Câmera › Formatos, escolha “Mais compatível”, e tire a foto de novo.",
        false
      );
    }
    throw new ImagemInvalidaError(
      "Não foi possível processar a imagem. Tire outra foto e tente novamente."
    );
  }
}

/**
 * Crop the video frame to the guide rectangle area, returning a JPEG blob.
 * rect coordinates are relative to the video's natural dimensions.
 */
export function cropToRect(
  videoElement: HTMLVideoElement,
  rect: { x: number; y: number; w: number; h: number }
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = rect.w;
  canvas.height = rect.h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.reject(new Error("Canvas 2D context unavailable"));
  ctx.drawImage(videoElement, rect.x, rect.y, rect.w, rect.h, 0, 0, rect.w, rect.h);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas toBlob failed"))),
      "image/jpeg",
      0.85
    );
  });
}

/**
 * Calculate luminance standard deviation for a region of pixels.
 * High stddev = content present (text, edges); low stddev = empty/uniform area.
 */
export function calcLuminanceStdDev(imageData: ImageData): number {
  const { data, width, height } = imageData;
  const pixelCount = width * height;
  if (pixelCount === 0) return 0;

  // Sample every 4th pixel for performance on mobile
  let sum = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += 16) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    sum += r * 0.299 + g * 0.587 + b * 0.114;
    count++;
  }
  const mean = sum / count;

  let varianceSum = 0;
  for (let i = 0; i < data.length; i += 16) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const lum = r * 0.299 + g * 0.587 + b * 0.114;
    varianceSum += (lum - mean) ** 2;
  }
  return Math.sqrt(varianceSum / count);
}

/** Format byte size for display (e.g., "287 KB") */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${Math.round(bytes / 1024)} KB`;
}
